package com.evoapps.evoshift.domain.timeline

import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterStatus
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.datetime.minus
import kotlinx.datetime.plus

/** SH-212: one change to a timeline. */
sealed interface TimelineEdit {
    /** A new entry (a note gets its id when saved). */
    data class Add(val entry: TimelineEntry) : TimelineEdit

    /** The entry [id] replaced by [entry]: moved (dates only) or changed. */
    data class Replace(val id: String, val entry: TimelineEntry) : TimelineEdit

    data class Remove(val id: String) : TimelineEdit
}

/**
 * What is wrong with a timeline an edit would leave; [entryId] the entry it is about, when one.
 * SH-234: [on] is that entry's own day and [limit] the day it collides with (the start or end of
 * cooperation, the person's first or last shift), so the message can say which day to pick;
 * [entryKind] the entry's kind, for the wording (a contract or a pay change).
 */
data class TimelineProblem(
    val kind: Kind,
    val entryId: String? = null,
    val on: LocalDate? = null,
    val limit: LocalDate? = null,
    val entryKind: TimelineKind? = null
) {
    enum class Kind {
        /** A day the entry needs is missing (the hire, an end, a note's day, a validity). */
        DATE_MISSING,
        /** The end (or "valid until") is before the start; the end of cooperation before the hire. */
        END_BEFORE_START,
        /** Two contracts (or pay terms) from the same day, or two spans of a status that overlap. */
        OVERLAP,
        /** SH-234: a contract or pay change before the start of cooperation ([limit]: the hire). */
        BEFORE_HIRE,
        /** SH-234: a contract or pay change after the end of cooperation ([limit]: the end). */
        AFTER_END,
        /** The last contract or pay terms removed: the pay terms need one of each. */
        REQUIRED,
        /** Contract, pay and payroll facts need pay terms at the workplace first. */
        NO_PAY_TERMS,
        /** The hire cannot be removed while the person has shifts here. */
        HIRE_HAS_SHIFTS,
        /** The hire after the person's first shift here. */
        HIRE_AFTER_SHIFTS,
        /** The end of cooperation before the person's last shift here. */
        END_BEFORE_SHIFTS,
        /** A note without text, or longer than [TimelineRules.NOTE_MAX_LENGTH]. */
        NOTE_EMPTY,
        NOTE_TOO_LONG,
        /** More notes than [TimelineRules.MAX_NOTES]. */
        TOO_MANY
    }
}

/** An edit the timeline refuses ([problems] say why, the first one is shown). */
class InvalidTimelineException(val problems: List<TimelineProblem>) : IllegalArgumentException("Invalid timeline: $problems")

/**
 * What a change reprices: shifts from [from] on (null: from the start, everything still open).
 * Only open months are repriced; LOCKED and ARCHIVED ones keep their snapshots.
 */
data class RecomputeTrigger(val from: LocalDate?)

object TimelineRules {
    const val NOTE_MAX_LENGTH = 500
    const val MAX_NOTES = 50

    /**
     * [timeline]'s entries after [edit] (SH-232: "not counted" in whole months, [snapped]).
     * SH-234: moving the start of cooperation takes the first contract and the first pay with it
     * ([anchoredToHire]).
     */
    fun apply(timeline: EmployeeTimeline, edit: TimelineEdit): List<TimelineEntry> {
        val entries = when (edit) {
            is TimelineEdit.Add -> timeline.entries + snapped(edit.entry)
            is TimelineEdit.Replace -> timeline.entries.map { if (it.id == edit.id) snapped(edit.entry) else it }
            is TimelineEdit.Remove -> timeline.entries.filterNot { it.id == edit.id }
        }
        val movesHire = edit is TimelineEdit.Replace && edit.id == TimelineKind.HIRE.name && edit.entry.kind == TimelineKind.HIRE
        return if (movesHire) anchoredToHire(entries, timeline.hire?.from, (edit as TimelineEdit.Replace).entry.from) else entries
    }

    /**
     * SH-234: an engagement has a contract and pay from its first day, so the first contract and
     * the first pay are anchored to the start of cooperation: when the hire moves from [oldHire]
     * to [newHire], the first dated entry of each lane that started with it (on the old day, or
     * before the new one) moves to the new day too. A lane with an entry "from the start" already
     * follows the hire. A lane whose second entry lies on or before the new day stays as it is
     * (the check then says which entry is before the start).
     */
    private fun anchoredToHire(entries: List<TimelineEntry>, oldHire: LocalDate?, newHire: LocalDate?): List<TimelineEntry> {
        if (newHire == null || newHire == oldHire) return entries
        var result = entries
        listOf(TimelineKind.CONTRACT, TimelineKind.PAY).forEach { kind ->
            val lane = result.filter { it.kind == kind }
            if (lane.isEmpty() || lane.any { it.from == null }) return@forEach
            val sorted = lane.sortedBy { it.from }
            val first = sorted.first()
            val start = first.from ?: return@forEach
            val anchored = start == oldHire || start < newHire
            val nextReached = sorted.getOrNull(1)?.from?.let { it <= newHire } == true
            if (anchored && !nextReached) {
                result = result.map { if (it === first) it.copy(from = newHire) else it }
            }
        }
        return result
    }

    /**
     * SH-232: [entry] as it is stored. Counting in the labour cost and SPMH applies to whole
     * months (SH-206: a month's labour % and SPMH always describe the same people), so a "not
     * counted" span starts on the first day of its month and ends on the last day of its month.
     */
    fun snapped(entry: TimelineEntry): TimelineEntry {
        if (entry.kind != TimelineKind.NOT_COUNTED) return entry
        return entry.copy(
            from = entry.from?.let { LocalDate(it.year, it.month, 1) },
            until = entry.until?.let { LocalDate(it.year, it.month, 1).plus(DatePeriod(months = 1)).minus(DatePeriod(days = 1)) }
        )
    }

    /** Everything wrong with [after] (the timeline an edit leaves); [shifts]: where the person's shifts lie. */
    fun problems(after: EmployeeTimeline, shifts: ShiftDays, removedHire: Boolean = false): List<TimelineProblem> = buildList {
        val entries = after.entries
        if (removedHire && shifts.any) add(TimelineProblem(TimelineProblem.Kind.HIRE_HAS_SHIFTS, TimelineKind.HIRE.name))
        val hires = entries.filter { it.kind == TimelineKind.HIRE }
        val ends = entries.filter { it.kind == TimelineKind.END }
        if (hires.size > 1) add(TimelineProblem(TimelineProblem.Kind.OVERLAP, TimelineKind.HIRE.name))
        if (ends.size > 1) add(TimelineProblem(TimelineProblem.Kind.OVERLAP, TimelineKind.END.name))
        val hire = hires.firstOrNull()
        val end = ends.firstOrNull()
        // A hire without a day comes from profiles written before engagements had one: it stays as it is.
        val hireDay = hire?.from
        val endDay = end?.from
        if (hireDay != null && endDay != null && endDay < hireDay) add(TimelineProblem(TimelineProblem.Kind.END_BEFORE_START, end.id, endDay, hireDay))
        if (hireDay != null && shifts.first != null && hireDay > shifts.first) add(TimelineProblem(TimelineProblem.Kind.HIRE_AFTER_SHIFTS, hire.id, hireDay, shifts.first))
        if (endDay != null && shifts.last != null && endDay < shifts.last) add(TimelineProblem(TimelineProblem.Kind.END_BEFORE_SHIFTS, end.id, endDay, shifts.last))

        entries.forEach { e ->
            if (e.kind.needsPayTerms && !after.hasPayTerms) add(TimelineProblem(TimelineProblem.Kind.NO_PAY_TERMS, e.id))
            if (e.kind.hasEnd && e.from != null && e.until != null && e.until < e.from) add(TimelineProblem(TimelineProblem.Kind.END_BEFORE_START, e.id))
            if (e.kind.staffCheck != null && e.until == null) add(TimelineProblem(TimelineProblem.Kind.DATE_MISSING, e.id))
            if (e.kind == TimelineKind.NOTE) {
                if (e.from == null) add(TimelineProblem(TimelineProblem.Kind.DATE_MISSING, e.id))
                val text = e.text.orEmpty().trim()
                if (text.isEmpty()) add(TimelineProblem(TimelineProblem.Kind.NOTE_EMPTY, e.id))
                if (text.length > NOTE_MAX_LENGTH) add(TimelineProblem(TimelineProblem.Kind.NOTE_TOO_LONG, e.id))
            }
        }
        if (entries.count { it.kind == TimelineKind.NOTE } > MAX_NOTES) add(TimelineProblem(TimelineProblem.Kind.TOO_MANY))

        // Sequential lanes: one entry per start day, and at least one while there are pay terms.
        listOf(TimelineKind.CONTRACT, TimelineKind.PAY).forEach { kind ->
            val list = entries.filter { it.kind == kind }
            // Within cooperation. Before the start, the latest change first: once it is moved or
            // removed, the first terms follow the start again (anchoredToHire).
            if (hireDay != null) {
                list.filter { it.from != null && it.from < hireDay }.sortedByDescending { it.from }
                    .forEach { add(TimelineProblem(TimelineProblem.Kind.BEFORE_HIRE, it.id, it.from, hireDay, kind)) }
            }
            if (endDay != null) {
                list.filter { it.from != null && it.from > endDay }.sortedBy { it.from }
                    .forEach { add(TimelineProblem(TimelineProblem.Kind.AFTER_END, it.id, it.from, endDay, kind)) }
            }
            list.groupBy { it.from }.values.filter { it.size > 1 }.forEach {
                add(TimelineProblem(TimelineProblem.Kind.OVERLAP, it.last().id, it.last().from, it.last().from, kind))
            }
            if (after.hasPayTerms && list.isEmpty()) add(TimelineProblem(TimelineProblem.Kind.REQUIRED, kind.name, entryKind = kind))
        }
        // Statuses, reliefs, working without pay, not counted: their spans of one lane do not overlap.
        entries.filter { it.kind.isSpan }.groupBy { it.lane }.values.forEach { lane ->
            val sorted = lane.sortedWith(compareBy(nullsFirst()) { it.from })
            sorted.zipWithNext().forEach { (a, b) ->
                if (a.until == null || (b.from == null) || b.from <= a.until) add(TimelineProblem(TimelineProblem.Kind.OVERLAP, b.id))
            }
        }
    }.distinct()

    /**
     * SH-234: what refuses the edit from [before] to [after]: the [problems] the edit brings in.
     * A problem the timeline already had stays allowed while the entry it is about is untouched,
     * so data from before (a raise typed before the hire on the employee form, an old end of
     * cooperation) never blocks an unrelated change, nor the change that puts it right.
     */
    fun refusals(before: EmployeeTimeline, after: EmployeeTimeline, shifts: ShiftDays, removedHire: Boolean = false): List<TimelineProblem> {
        val existing = problems(before, shifts)
        return problems(after, shifts, removedHire).filterNot { problem ->
            existing.any { it.kind == problem.kind && it.entryId == problem.entryId } &&
                (problem.entryId == null || before.entry(problem.entryId) == after.entry(problem.entryId))
        }
    }

    /**
     * The first day that costs differently after a change from [before] to [after]: the cost
     * lanes (contract, pay, student status, tax reliefs) are compared on every day one of their
     * entries starts or ends; null when the change does not touch what shifts cost (hire, end,
     * OHS, medical exam, notes).
     */
    fun recompute(before: EmployeeTimeline, after: EmployeeTimeline): RecomputeTrigger? {
        val days = mutableSetOf<LocalDate>()
        (before.entries + after.entries).filter { it.kind.affectsCost }.forEach { e ->
            e.from?.let { days += it }
            e.until?.let { days += it.plus(DatePeriod(days = 1)) }
        }
        if (before.costStateOn(null) != after.costStateOn(null)) return RecomputeTrigger(null)
        val first = days.sorted().firstOrNull { before.costStateOn(it) != after.costStateOn(it) } ?: return null
        return RecomputeTrigger(first)
    }

    /**
     * The months [trigger] reprices among [rosters]: the open ones (not LOCKED, not ARCHIVED) from
     * its month on, as RepriceFutureShiftsUseCase does. A locked month keeps its snapshots.
     */
    fun monthsToRecompute(rosters: List<Roster>, trigger: RecomputeTrigger): List<Roster> {
        val first = trigger.from?.let { it.year * 12 + it.month.number - 1 }
        return rosters.filter { it.status != RosterStatus.LOCKED && it.status != RosterStatus.ARCHIVED }
            .filter { first == null || it.year * 12 + it.month - 1 >= first }
            .sortedBy { it.year * 12 + it.month }
    }
}
